import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useTheme } from '../contexts/ThemeContext';

const CustomCursor = () => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [cursorType, setCursorType] = useState('default'); // default, pointer, text
  const [isMobile, setIsMobile] = useState(false);
  const cursorRef = useRef(null);
  const dotRef = useRef(null);
  const { currentTheme } = useTheme();
  const rafRef = useRef(null);

  // Проверка на мобильное устройство
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Проверяем при загрузке
    checkIfMobile();
    
    // Проверяем при изменении размера окна
    window.addEventListener('resize', checkIfMobile);
    
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);

  // Оптимизированный обработчик движения мыши с debounce через requestAnimationFrame
  const handleMouseMove = useCallback((e) => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }
    
    rafRef.current = requestAnimationFrame(() => {
      setPosition({ x: e.clientX, y: e.clientY });
    });
  }, []);

  // Обновление положения курсора при движении мыши
  useEffect(() => {
    // Добавляем обработчики только если не мобильное устройство
    if (!isMobile) {
      document.addEventListener('mousemove', handleMouseMove, { passive: true });

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
        }
      };
    }
    
    return () => {};
  }, [handleMouseMove, isMobile]);

  // Анимированное следование за курсором
  useEffect(() => {
    if (!isMobile && cursorRef.current && dotRef.current) {
      const cursor = cursorRef.current;
      const dot = dotRef.current;
      
      cursor.style.transform = `translate(-50%, -50%) translate(${position.x}px, ${position.y}px)`;
      dot.style.transform = `translate(-50%, -50%) translate(${position.x}px, ${position.y}px)`;
    }
  }, [position, isMobile]);

  // Обработка наведения на различные элементы
  useEffect(() => {
    if (isMobile) return;
    
    // Функции управления состоянием курсора
    const setToDefault = () => setCursorType('default');
    const setToPointer = () => setCursorType('pointer');
    const setToText = () => setCursorType('text');
    
    // Интерактивные элементы (кнопки, ссылки и т.д.)
    const interactiveElements = document.querySelectorAll(
      'a, button, [role="button"], input[type="submit"], input[type="button"], input[type="checkbox"], input[type="radio"], select, details, summary, .clickable, [tabindex]:not([tabindex="-1"])'
    );
    
    // Текстовые поля
    const textElements = document.querySelectorAll(
      'input[type="text"], input[type="email"], input[type="password"], input[type="search"], input[type="tel"], input[type="url"], input[type="number"], textarea, [contenteditable="true"]'
    );
    
    // Активируем нужное состояние курсора для каждого типа элементов
    interactiveElements.forEach(element => {
      element.addEventListener('mouseenter', setToPointer);
      element.addEventListener('mouseleave', setToDefault);
    });
    
    textElements.forEach(element => {
      element.addEventListener('mouseenter', setToText);
      element.addEventListener('mouseleave', setToDefault);
    });
    
    return () => {
      // Очистка обработчиков
      interactiveElements.forEach(element => {
        element.removeEventListener('mouseenter', setToPointer);
        element.removeEventListener('mouseleave', setToDefault);
      });
      
      textElements.forEach(element => {
        element.removeEventListener('mouseenter', setToText);
        element.removeEventListener('mouseleave', setToDefault);
      });
    };
  }, [isMobile]);

  // Классы для разных состояний курсора в зависимости от темы
  const cursorClasses = useMemo(() => {
    const baseClass = 'custom-cursor';
    
    // Основные цвета в зависимости от темы
    const colorClass = currentTheme === 'light' 
      ? 'border-[#1A56DB]' 
      : 'border-[#00F0FF]';
      
    // Разные стили в зависимости от типа элемента под курсором
    switch(cursorType) {
      case 'pointer':
        return `${baseClass} pointer ${colorClass} ${
          currentTheme === 'light' 
            ? 'bg-light-secondary/10 border-light-secondary'
            : 'bg-dark-secondary/10 border-dark-secondary'
        }`;
      case 'text':
        return `${baseClass} text ${colorClass}`;
      default:
        return `${baseClass} ${colorClass}`;
    }
  }, [cursorType, currentTheme]);
  
  // Классы для точки в зависимости от темы
  const dotClasses = useMemo(() => {
    const baseClass = 'cursor-dot';
    
    const dotColor = currentTheme === 'light'
      ? 'bg-light-secondary'
      : 'bg-dark-secondary';
      
    return `${baseClass} ${dotColor} ${cursorType === 'text' ? 'opacity-0' : ''}`;
  }, [cursorType, currentTheme]);

  // Если мобильное устройство, не отображаем кастомный курсор
  if (isMobile) {
    return null;
  }

  return (
    <>
      <div 
        ref={cursorRef}
        className={cursorClasses}
        style={{ left: 0, top: 0 }}
      ></div>
      <div 
        ref={dotRef}
        className={dotClasses}
        style={{ left: 0, top: 0 }}
      ></div>
    </>
  );
};

export default CustomCursor; 